import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { useDebounce } from "use-debounce";
import { Search, X, ChefHat, Package } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { VirtualScroller } from "@/components/common/VirtualScroller";
import { adminApi } from "@/lib/api/admin-api";
import { recipeRequests } from "@/lib/api/requests/recipes";
import type { Material } from "@/types/material";
import type { Recipe } from "@/types/recipe";
import { cn } from "@/lib/utils";
import { UnitSelect } from "@/components/units/UnitSelect";
import { useUnits } from "@/hooks/useUnits";
import { UnitConversionWarning } from "@/components/units/UnitConversionWarning";
import { recipeUnitFor, subRecipeUnitFor, unitMayNotConvert } from "@/lib/units/unit-options";

type Section = "ingredient" | "recipe";

type Selection =
  | { type: "ingredient"; material: Material }
  | { type: "recipe"; recipe: Recipe };

interface AddSubIngredientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentMaterial: Material;
}

export function AddSubIngredientDialog({
  open,
  onOpenChange,
  parentMaterial,
}: AddSubIngredientDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();

  const [section, setSection] = useState<Section>("ingredient");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("");

  const { groups: unitGroups } = useUnits(open, "all");
  // Every measuring unit, whatever the item is: a case is bought, not measured,
  // so the container family is left out. Units costing may not convert are
  // warned about below the picker instead of being hidden.
  const measureGroups = useMemo(
    () => unitGroups.filter((group) => group.family !== "container"),
    [unitGroups],
  );
  const pricedUnit =
    selection?.type === "ingredient"
      ? (selection.material.effectiveUnitCost?.unit ?? null)
      : selection?.type === "recipe"
        ? subRecipeUnitFor(selection.recipe)
        : null;
  const unitMismatch =
    selection !== null &&
    unit.trim() !== "" &&
    pricedUnit !== null &&
    unitMayNotConvert(unitGroups, unit, pricedUnit, {
      densityGPerMl: selection.type === "ingredient" ? selection.material.densityGPerMl : null,
      pack:
        selection.type === "ingredient"
          ? {
              purchaseUom: selection.material.unitOfMeasure,
              packQuantity: selection.material.packQuantity,
              packUom: selection.material.packUom ?? "",
            }
          : null,
    });

  const existingChildIds = useMemo(
    () => new Set(parentMaterial.subIngredients?.map((si) => si.childMaterialId) ?? []),
    [parentMaterial.subIngredients],
  );

  const mutation = useMutation({
    mutationFn: () => {
      if (!selection) throw new Error("No selection");
      return adminApi.addSubIngredient(parentMaterial.id, {
        ...(selection.type === "ingredient"
          ? { childMaterialId: selection.material.id }
          : { childRecipeId: selection.recipe.id }),
        quantity: parseFloat(qty),
        unit,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
      toast.success(t`Sub-ingredient added`);
      handleClose();
    },
    onError: () => toast.error(t`Failed to add sub-ingredient`),
  });

  function handleClose() {
    onOpenChange(false);
    setSection("ingredient");
    setSearch("");
    setSelection(null);
    setQty("");
    setUnit("");
  }

  function handleSwitchSection(next: Section) {
    setSection(next);
    setSearch("");
    setSelection(null);
    setQty("");
    setUnit("");
  }

  const selectionLabel =
    selection?.type === "ingredient"
      ? selection.material.name
      : selection?.recipe.name ?? "";

  const selectionUnit =
    selection?.type === "ingredient" ? selection.material.unitOfMeasure : "";

  const parsedQty = parseFloat(qty);
  const canSubmit =
    selection !== null &&
    qty.trim() !== "" &&
    !isNaN(parsedQty) &&
    parsedQty > 0 &&
    unit.trim() !== "";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t`Add sub-ingredient`}</DialogTitle>
        </DialogHeader>

        {/* Section toggle */}
        <div className="flex rounded-lg border bg-(--color-surface-muted) p-1 gap-1">
          <button
            type="button"
            onClick={() => handleSwitchSection("ingredient")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              section === "ingredient"
                ? "bg-(--color-surface-content) shadow-sm text-(--color-text-primary)"
                : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
            )}
          >
            <Package className="size-3.5" />
            {t`Ingredient`}
          </button>
          <button
            type="button"
            onClick={() => handleSwitchSection("recipe")}
            className={cn(
              "flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              section === "recipe"
                ? "bg-(--color-surface-content) shadow-sm text-(--color-text-primary)"
                : "text-(--color-text-secondary) hover:text-(--color-text-primary)",
            )}
          >
            <ChefHat className="size-3.5" />
            {t`Recipe`}
          </button>
        </div>

        <div className="space-y-4">
          {/* Picker */}
          <div className="space-y-1.5">
            <Label>
              {section === "ingredient" ? t`Select ingredient` : t`Select recipe`}
            </Label>

            {selection ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 bg-(--color-surface-muted)/40">
                <div className="flex items-center gap-2">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-(--color-surface-muted) text-xs font-semibold text-(--color-text-secondary)">
                    {selectionLabel.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-medium">{selectionLabel}</span>
                  {selectionUnit && (
                    <span className="text-xs text-(--color-text-secondary)">{selectionUnit}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { setSelection(null); setUnit(""); setSearch(""); }}
                  className="text-(--color-text-secondary) hover:text-(--color-text-primary)"
                >
                  <X className="size-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 size-4 text-(--color-text-secondary) pointer-events-none" />
                  <Input
                    className="pl-8"
                    placeholder={
                      section === "ingredient"
                        ? t`Search ingredients…`
                        : t`Search recipes…`
                    }
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                </div>

                {/* Results */}
                <div className="rounded-md border bg-(--color-surface-content) shadow-md max-h-48 overflow-y-auto">
                  {section === "ingredient" && (
                    <VirtualScroller<Material>
                      queryKey={["admin", "materials", "sub-picker", debouncedSearch]}
                      queryFn={(page, pageSize) =>
                        adminApi.getMaterials({
                          search: debouncedSearch || undefined,
                          page,
                          limit: pageSize,
                        })
                      }
                      pageSize={20}
                      enabled={open}
                      emptyMessage={t`No ingredients found`}
                      loadingRows={3}
                      renderSkeleton={() => (
                        <div className="h-11 mx-2 my-1 rounded bg-(--color-surface-muted) animate-pulse" />
                      )}
                      renderItem={(mat) => {
                        if (mat.id === parentMaterial.id || existingChildIds.has(mat.id)) {
                          return null;
                        }
                        return (
                          <ResultItem
                            key={mat.id}
                            primary={mat.name}
                            secondary={mat.unitOfMeasure}
                            onClick={() => {
                              setSelection({ type: "ingredient", material: mat });
                              setUnit(recipeUnitFor(mat));
                              setSearch("");
                            }}
                          />
                        );
                      }}
                    />
                  )}

                  {section === "recipe" && (
                    <VirtualScroller<Recipe>
                      queryKey={["recipes", "sub-picker", debouncedSearch]}
                      queryFn={(page, pageSize) =>
                        recipeRequests.list({
                          search: debouncedSearch || undefined,
                          page,
                          limit: pageSize,
                        })
                      }
                      pageSize={20}
                      enabled={open}
                      emptyMessage={t`No recipes found`}
                      loadingRows={3}
                      renderSkeleton={() => (
                        <div className="h-11 mx-2 my-1 rounded bg-(--color-surface-muted) animate-pulse" />
                      )}
                      renderItem={(rec) => {
                        if (rec.materialId != null && existingChildIds.has(rec.materialId)) {
                          return null;
                        }
                        return (
                          <ResultItem
                            key={rec.id}
                            primary={rec.name}
                            secondary={rec.category ?? ""}
                            onClick={() => {
                              setSelection({ type: "recipe", recipe: rec });
                              setUnit(subRecipeUnitFor(rec) ?? "");
                              setSearch("");
                            }}
                          />
                        );
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {/* Qty + unit */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sub-qty">{t`Quantity`}</Label>
              <Input
                id="sub-qty"
                type="number"
                min="0"
                step="any"
                placeholder="400"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sub-unit">{t`Unit`}</Label>
              <UnitSelect
                id="sub-unit"
                label={t`Unit`}
                value={unit}
                onChange={setUnit}
                groups={measureGroups}
                disabled={selection === null}
                placeholder={selection === null ? t`Choose an item first` : t`Select a unit...`}
                className="rounded-md border-(--color-border) bg-(--color-surface-content)"
              />
              {unitMismatch && pricedUnit !== null && (
                <UnitConversionWarning
                  unit={unit}
                  pricedUnit={pricedUnit}
                  isRecipe={selection?.type === "recipe"}
                />
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={mutation.isPending}>
            {t`Cancel`}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
          >
            {mutation.isPending ? t`Adding…` : t`Add`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultItem({
  primary,
  secondary,
  onClick,
}: {
  primary: string;
  secondary: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-2.5 px-3 py-2 text-sm hover:bg-(--color-surface-accent) transition-colors text-left"
      onClick={onClick}
    >
      <div className="flex size-6 shrink-0 items-center justify-center rounded bg-(--color-surface-muted) text-xs font-semibold text-(--color-text-secondary)">
        {primary.charAt(0).toUpperCase()}
      </div>
      <span className="flex-1 truncate font-medium">{primary}</span>
      {secondary && (
        <span className="text-xs text-(--color-text-secondary)">{secondary}</span>
      )}
    </button>
  );
}
